import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, VerificationRequiredError } from "@/hooks/useAuth";
import { seed, ApiError } from "@/lib/api";
import { Eye, EyeOff, Zap } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]   = useState(false);
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      if (err instanceof VerificationRequiredError) {
        navigate(`/verify-email?email=${encodeURIComponent(err.email)}`);
        return;
      }
      setError(err instanceof ApiError ? err.message : "Login failed");
    }
    finally { setLoading(false); }
  };

  const handleSeed = async () => {
    setSeeding(true); setError("");
    try {
      const res = await seed();
      await login(res.credentials.email, res.credentials.password);
      navigate("/dashboard");
    } catch (err) { setError(err instanceof ApiError ? err.message : "Demo setup failed"); }
    finally { setSeeding(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-paper">

      {/* Left panel — branding (desktop only) */}
      <div className="hidden lg:flex lg:w-[45%] bg-ink flex-col justify-between p-12 xl:p-16">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-coral rounded-xl flex items-center justify-center text-white font-display font-bold text-base -rotate-2 shadow-lg shadow-coral/40">M</div>
          <span className="font-display font-bold text-2xl text-white tracking-tight">MoreMail</span>
        </Link>
        <div>
          <blockquote className="text-white/90 font-display text-2xl xl:text-3xl font-bold leading-snug mb-6">
            "Our open rates went from 18% to 41% in the first month."
          </blockquote>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-coral/20 border border-coral/30 flex items-center justify-center text-coral font-bold text-sm">JL</div>
            <div>
              <p className="text-white font-semibold text-sm">Jamie Lin</p>
              <p className="text-white/40 text-xs">Growth Lead, Pallet</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-6">
          {[["42%","Avg open rate"],["12K+","Teams using it"],["3.2×","More conversions"]].map(([v,l])=>(
            <div key={l}>
              <p className="text-white font-display font-bold text-3xl">{v}</p>
              <p className="text-white/40 text-xs mt-1 uppercase tracking-wider font-semibold">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8">
        {/* Mobile logo */}
        <Link to="/" className="flex items-center gap-2 mb-10 lg:hidden">
          <div className="w-9 h-9 bg-coral rounded-xl flex items-center justify-center text-white font-display font-bold text-base -rotate-2 shadow-md shadow-coral/30">M</div>
          <span className="font-display font-bold text-2xl tracking-tight">MoreMail</span>
        </Link>

        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-bold text-ink mb-1.5">Welcome back</h1>
          <p className="text-sm text-slate mb-8">Sign in to your MoreMail account</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl mb-5">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all"
                placeholder="you@company.com" autoComplete="email" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all pr-10"
                  placeholder="••••••••" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-ink">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-full bg-coral text-white font-semibold text-sm hover:bg-coral-dark disabled:opacity-60 transition-colors shadow-md shadow-coral/25">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-black/[0.06]" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-paper px-3 text-slate">or</span>
            </div>
          </div>

          <button onClick={handleSeed} disabled={seeding}
            className="w-full py-3 rounded-full border-2 border-teal/20 text-teal font-semibold text-sm hover:bg-teal-light disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
            <Zap size={15} />
            {seeding ? "Setting up demo…" : "Quick demo — seed data & sign in"}
          </button>

          <p className="text-center text-sm text-slate mt-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-coral font-semibold hover:underline">Sign up free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
